import React from "react";
import styled from "styled-components";
import moment from "moment";
import Moment from "react-moment";
import { Flex, Box } from "grid-styled";
import { Helmet } from "react-helmet";
import { graphql, compose } from "react-apollo";
import { gql } from "apollo-boost";
import Modal from "./Modal";
import { Table, TableRow, TableCell } from "./Table";
import { A } from "./Link";
import Button from "./Button";
import CenteredLoader from "./CenteredLoader";
import { InputContainer, DatePicker, Input } from "./forms";
import Icon from "./Icon";
import { CLEAR } from "../icons";
import Text from "./Text";

const columnSizing = "1fr auto";

const HolidayRow = styled(TableRow)`
  ${Button} {
    opacity: 0;
    transition: opacity 0.2s;
  }

  &:hover ${Button} {
    opacity: 1;
  }
`;

class Holidays extends React.Component {
  state = {
    editingHoliday: null,
    date: null
  };

  handleChange = date => {
    this.setState({ date });
  };

  handleSubmit = async e => {
    if (e) e.preventDefault();

    const { editingHoliday, date: dateMoment } = this.state;
    const date = dateMoment.format("YYYY-MM-DD");

    try {
      if (editingHoliday === "new") {
        await this.props.createHoliday({ date });
      } else {
        await this.props.updateHoliday(editingHoliday, { date });
      }
      this.setState({
        editingHoliday: null,
        date: null
      });
      this.props.refetch();
    } catch (err) {
      // TODO: Do something here
    }
  };

  newHoliday = () => {
    this.setState({
      editingHoliday: "new",
      date: moment()
    });
  };

  editHoliday = holiday => () => {
    this.setState({
      editingHoliday: holiday.id,
      date: holiday.date
    });
  };

  deleteHoliday = holiday => async () => {
    if (
      window.confirm(
        `Are you sure you want to remove ${moment(holiday.date).format(
          "M-D-YYYY"
        )} as a holiday?`
      )
    ) {
      await this.props.deleteHoliday(holiday.id);
      this.props.refetch();
    }
  };

  closeModal = () => {
    this.setState({
      editingHoliday: null,
      date: null
    });
  };

  renderHoliday = holiday => (
    <HolidayRow key={`holiday-${holiday.id}`} columns={columnSizing}>
      <TableCell>
        <A onClick={this.editHoliday(holiday)}>
          <Moment format="M/D/YYYY">{holiday.date}</Moment>
        </A>
      </TableCell>
      <TableCell>
        <Button danger hasIcon onClick={this.deleteHoliday(holiday)}>
          <Icon icon={CLEAR} />
        </Button>
      </TableCell>
    </HolidayRow>
  );

  render() {
    const { editingHoliday, date } = this.state;
    const { loading, holidays } = this.props;

    const modalTitle = `${
      editingHoliday === "new" ? "New" : "Editing"
    } Holiday`;

    return (
      <Box p={3}>
        <Helmet>
          <title>Holidays</title>
        </Helmet>
        <Flex pb={2} justify="flex-end">
          <Button primary onClick={this.newHoliday}>
            Add Holiday
          </Button>
        </Flex>
        <Table noSideBorders>
          {loading && !holidays ? (
            <CenteredLoader />
          ) : (
            <React.Fragment>
              <TableRow header columns={columnSizing}>
                <TableCell>Date</TableCell>
              </TableRow>
              {holidays && holidays.length ? (
                holidays.map(this.renderHoliday)
              ) : (
                <TableRow columns={columnSizing}>
                  <TableCell>
                    <Text muted>No holidays</Text>
                  </TableCell>
                </TableRow>
              )}
            </React.Fragment>
          )}
        </Table>
        <Modal
          isOpen={editingHoliday !== null}
          title={modalTitle}
          close={this.closeModal}
        >
          <form method="POST" onSubmit={this.handleSubmit}>
            <Box p={2}>
              {editingHoliday && (
                <InputContainer
                  name="date"
                  labelText="Date"
                  value={date}
                  onChange={this.handleChange}
                  fieldComponent={DatePicker}
                />
              )}
              <Flex mt={2} justify="flex-end">
                <Button primary type="submit">
                  {editingHoliday === "new" ? "Create" : "Update"} Holiday
                </Button>
              </Flex>
            </Box>
          </form>
        </Modal>
      </Box>
    );
  }
}

const HolidaysQuery = gql`
  query HolidaysQuery {
    holidays {
      id
      date
    }
  }
`;

const CreateHolidayMutation = gql`
  mutation CreateHolidayMutation($input: HolidayInput!) {
    createHoliday(input: $input) {
      id
      date
    }
  }
`;

const UpdateHolidayMutation = gql`
  mutation UpdateHolidayMutation($id: ID!, $input: HolidayInput!) {
    updateHoliday(id: $id, input: $input) {
      id
      date
    }
  }
`;

const DeleteHolidayMutation = gql`
  mutation DeleteHolidayMutation($id: ID!) {
    deleteHoliday(id: $id) {
      id
      date
    }
  }
`;

const enhance = compose(
  graphql(CreateHolidayMutation, {
    props({ mutate }) {
      return {
        createHoliday(input) {
          return mutate({
            variables: { input }
          });
        }
      };
    }
  }),
  graphql(UpdateHolidayMutation, {
    props({ mutate }) {
      return {
        updateHoliday(id, input) {
          return mutate({
            variables: { id, input }
          });
        }
      };
    }
  }),
  graphql(DeleteHolidayMutation, {
    props({ mutate }) {
      return {
        deleteHoliday(id) {
          return mutate({
            variables: { id }
          });
        }
      };
    }
  }),
  graphql(HolidaysQuery, {
    options: {
      fetchPolicy: "network-only"
    },
    props: ({ data: { loading, refetch, holidays } }) => ({
      loading,
      refetch,
      holidays: holidays
        ? [...holidays].sort(
            ({ date: dateA }, { date: dateB }) =>
              new Date(dateA) - new Date(dateB)
          )
        : null
    })
  })
);

export default enhance(Holidays);



// WEBPACK FOOTER //
// ./src/components/Holidays.js